import {
  AddHollow,
  CompaniesNav,
  Filter,
  FilterActive,
  TeamNav,
} from '@clearkit/icons';
import classnames from 'classnames';
import {
  CKActionMenu,
  CKBox,
  CKButton,
  CKContainerProps,
  CKCountUp,
  CKLayoutGridAuto,
  CKLayoutHorizontal,
  CKPopover,
} from 'clearkit';

import { hideListMedium, hideListSmall } from './constants';
import { PaginationButtons } from './PaginationButtons';

type ListHeaderProps = CKContainerProps & {
  hasFilters?: boolean;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  onNextPage?: () => void;
  onPreviousPage?: () => void;
  onToggleFilterSidebar?: () => void;
  pageEnd?: number;
  pageStart?: number;
  previousTotalCompanies?: number;
  previousTotalProspects?: number;
  showFilterSidebar?: boolean;
  totalCompanies?: number;
  totalProspects?: number;
};

const Count = ({
  className,
  children,
  end,
  label,
  start,
}: CKContainerProps & {
  end: number;
  label: string;
  start: number;
}) => (
  <CKLayoutHorizontal
    className={classnames('min-w-0 text-sm', className)}
    gap="0.5rem"
    justifyContent="start"
  >
    {children}
    <span>
      <CKCountUp duration={1000} end={end} start={start} />{' '}
      <span className={hideListSmall}>{label}</span>
    </span>
  </CKLayoutHorizontal>
);

const ActionMenu = (
  <CKActionMenu>
    <CKActionMenu.Item>Display all</CKActionMenu.Item>
    <CKActionMenu.Item>Display only selected</CKActionMenu.Item>
  </CKActionMenu>
);

export const ListHeader = ({
  className,
  hasFilters,
  hasNextPage,
  hasPreviousPage,
  pageStart,
  pageEnd,
  previousTotalCompanies = 0,
  previousTotalProspects = 0,
  onNextPage,
  onPreviousPage,
  onToggleFilterSidebar,
  showFilterSidebar,
  totalProspects = 0,
  totalCompanies = 0,
}: ListHeaderProps) => {
  return (
    <CKBox
      className={classnames(
        className,
        'px-6 py-4 !bg-white/40 backdrop-blur-lg sticky @item/list:top-6 top-8 z-10',
      )}
    >
      <CKLayoutHorizontal className="@item/list:flex-nowrap">
        <CKLayoutGridAuto
          autoRepeat="auto-fit"
          className="min-w-0 @item/list:grow-1 @item/list:basis-full max-w-[24rem]"
          minWidth="9rem"
        >
          <Count
            className="hidden @item/list:flex"
            end={totalCompanies}
            label={totalCompanies === 1 ? 'company' : 'companies'}
            start={previousTotalCompanies}
          >
            <CompaniesNav height={18} width={18} />
          </Count>
          <Count
            end={totalProspects}
            label={totalProspects === 1 ? 'prospect' : 'prospects'}
            start={previousTotalProspects}
          >
            <TeamNav height={18} width={18} />
          </Count>
        </CKLayoutGridAuto>
        <CKLayoutHorizontal className="basis-auto" justifyContent="end">
          <CKPopover popover={ActionMenu} wrapperClassName="relative">
            <CKPopover.Trigger
              className="!text-sm"
              variant="tertiary"
              variantColor="blue"
            >
              <span className={hideListSmall}>Displaying</span> {pageStart} -{' '}
              {pageEnd}
            </CKPopover.Trigger>
          </CKPopover>
          <PaginationButtons
            hasNextPage={hasNextPage}
            hasPreviousPage={hasPreviousPage}
            onNextPage={onNextPage}
            onPreviousPage={onPreviousPage}
          />
          {showFilterSidebar ? (
            <CKButton
              onClick={onToggleFilterSidebar}
              title="Close filters"
              variant="simple"
              variantColor="blue"
            >
              <CKLayoutHorizontal as="span" gap=".25rem" justifyContent="start">
                <FilterActive />
                <span className={hideListMedium}>Close filters</span>
              </CKLayoutHorizontal>
            </CKButton>
          ) : (
            <CKButton
              onClick={onToggleFilterSidebar}
              title={`${hasFilters ? 'Edit' : 'Add'} filters`}
              variant="simple"
              variantColor="blue"
            >
              <CKLayoutHorizontal as="span" gap=".25rem" justifyContent="start">
                {hasFilters ? <Filter /> : <AddHollow />}
                <span className={hideListMedium}>
                  {hasFilters ? 'Edit' : 'Add'} filters
                </span>
              </CKLayoutHorizontal>
            </CKButton>
          )}
        </CKLayoutHorizontal>
      </CKLayoutHorizontal>
    </CKBox>
  );
};
